/* eslint-disable consistent-return */
import isServerSideRendering from './isServerSideRendering';

const stripEquals = (val: string): string => (val || '').replace(/%3D/g, '');

/**
 * Creates a base64-encoded string (ASCII) after converting every character in
 * the provided string into a byte of binary data.
 *
 * @example
 * import { encodeString } from '@carvana/futilities/crypto';
 * encodeString('carvana')
 * // () => 'Y2FydmFuYQ=='
 * encodeString('{lorem: \'ipsum\'}')
 * // () => 'e2xvcmVtOiAnaXBzdW0nfQ=='
 * encodeString()
 * // () => undefined
 *
 * @kind function
 * @name decodeAndParse
 * @sig String -> String
 * @param {String} val A raw string to convert to base64
 * @returns {String} A base64-encoded string derived from the original string value
 */
function encodeString(val: string) {
  try {
    if (isServerSideRendering()) return;
    return window.btoa(val);
  } catch (err) {
    // String is probably not in a format that btoa() can encode it
  }
}

/**
 * Decodes a base64-encoded string into the original string (before it was encoded).
 *
 * @example
 * import { decodeString } from '@carvana/futilities/crypto';
 * decodeString('Y2FydmFuYQ==')
 * // "carvana"
 * decodeString('e2xvcmVtOiAnaXBzdW0nfQ==')
 * // "{lorem: 'ipsum'}"
 * decodeString()
 * // undefined
 *
 * @func
 * @sig String -> String
 * @param {String} val A base64-encoded string to decode
 * @returns {String} A string value that was decoded from a given base64-encoded string
 */
function decodeString(val: string) {
  try {
    if (isServerSideRendering()) return;
    return window.atob(stripEquals(val));
  } catch (err) {
    // String is probably not in a format that atob() can decode it
  }
}

/**
 * Converts JSON to a string _prior_ to creating a base64-encoded from it.
 * Non JSON values are still base64-encoded.
 *
 * @example
 * import { encodeAndStringify } from '@carvana/futilities/crypto';
 * encodeAndStringify('carvana')
 * // "Y2FydmFuYQ=="
 * encodeAndStringify({lorem: 'ipsum'})
 * // "e2xvcmVtOiAnaXBzdW0nfQ=="
 * encodeAndStringify()
 * // undefined
 *
 * @func
 * @sig String|{k: v} -> String
 * @param {String|Object|Array} val A value which may be an Object, Array, or String
 * @returns {String} A base64-encoded string derived from the original string value
 */
function encodeAndStringify(val: {} | any[] | string) {
  return val ? (encodeString(JSON.stringify(val)) as string) : '';
}

/**
 * Parses JSON (if necessary) _after_ decoding a base64-encoded string.
 * Non JSON values are returned as-is (after they've been decoded).
 *
 * @example
 * import { decodeAndParse } from '@carvana/futilities/crypto';
 * decodeAndParse('Y2FydmFuYQ==')
 * // "carvana"
 * decodeAndParse('e2xvcmVtOiAnaXBzdW0nfQ==')
 * // {lorem: 'ipsum'}
 * decodeAndParse()
 * // undefined
 *
 * @func
 * @sig String -> String|{k: v}
 * @param {String} val A base64-encoded string to decode
 * @returns {String|Object|Array} A string value that was decoded from a given base64-encoded string
 * and (if necessary) run through `JSON.parse()`
 */
function decodeAndParse(val: string) {
  return val ? JSON.parse(decodeString(val) as string) : {};
}

export {
  decodeString,
  encodeString,
  decodeAndParse,
  encodeAndStringify
};
